import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { queryClient } from "@/lib/query-client";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { dispatch } from "@/redux/store";
import { useApplyTPARulesMutation } from "@/hooks/mutation/estimates";
import { useTrackedWorkerJob } from "@/hooks/queries/jobs/useTrackedWorkerJob";
import type { Estimation } from "@/interfaces/estimation.interface";
import { useDetailsSectionData } from "./useDetailsSectionData";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";

export type SelectedTPAFields = { carrierId?: string; tpaId?: string };

/**
 * Drives the confirmation dialog:
 * - "confirm": awaiting the user's decision.
 * - "saving": the applyTPARules POST is in flight.
 * - "syncing": the change is accepted and the worker is recomputing line items (dialog stays open).
 * - "failed": the worker job failed; the user can retry or back out.
 */
export type TPARulesPhase = "confirm" | "saving" | "syncing" | "failed";

/**
 * Manages the entire state for updating tpa/carrier on an existing estimate. Maintains the selected
 * tpa/carrier in state until the user "confirms", then submits to the backend.
 *
 * The backend runs the (expensive) rules-engine recompute either inline or on a background worker. When
 * it queues a job, the dialog stays open in its "syncing" state and `useTrackedWorkerJob` follows that
 * job — then closes on success or shows a retry view on failure.
 */
export function useTPARules(estimateId: string) {
  const { estimation } = useDetailsSectionData(estimateId!);
  const { searchEstimateDetail } = useEstimateDetail(estimateId!);

  const [selectedTPAFields, setSelectedTPAFields] = useState<SelectedTPAFields>({
    carrierId: undefined,
    tpaId: undefined,
  });

  const { mutateAsync: applyTPARulesMutation } = useApplyTPARulesMutation();

  const [isTPARulesModalOpen, setIsTPARulesModalOpen] = useState(false);
  const [phase, setPhase] = useState<TPARulesPhase>("confirm");

  const previousEstimationRef = useRef<Estimation | null>(null);

  const resetTpaAndCarrier = useCallback(
    () =>
      setSelectedTPAFields({
        carrierId: estimation?.carrierId,
        tpaId: estimation?.tpaId,
      }),
    [estimation],
  );

  // Keep the local selection in sync with the persisted estimate, but only while idle — never while the
  // dialog is open or a recompute is in flight, or an optimistic update / rollback would clobber the
  // user's in-progress selection (and break "Try again", which re-submits it).
  useEffect(() => {
    if (!estimation) return;
    if (isTPARulesModalOpen || phase !== "confirm") return;
    resetTpaAndCarrier();
  }, [estimation, isTPARulesModalOpen, phase, resetTpaAndCarrier]);

  const updateSelectedTPAField = useCallback((field: keyof SelectedTPAFields, value: string) => {
    setSelectedTPAFields((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsTPARulesModalOpen(true);
  }, []);

  const refetchEstimate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
    queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detailMetadata(estimateId) });
    searchEstimateDetail(estimateId);
  }, [estimateId, searchEstimateDetail]);

  const rollbackOptimisticEstimation = useCallback(() => {
    const previous = previousEstimationRef.current;
    if (!previous) return;
    queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), previous);
    dispatch(estimationActions.setEstimationState({ estimation: previous }));
  }, [estimateId]);

  const finishTpaSync = useCallback(() => {
    // The worker persists the tpa/carrier + recomputed line items, so pull server truth in now.
    refetchEstimate();
    setPhase("confirm");
    setIsTPARulesModalOpen(false);
  }, [refetchEstimate]);

  // The worker's error is deliberately not surfaced — it's internal detail. The dialog shows a generic
  // failure the user can retry from.
  const failTpaSync = useCallback(() => {
    // A failed job means the server still holds the old tpa/carrier while our caches hold the optimistic
    // new one — snap back to the pre-change estimation and refetch to be safe. The dialog switches to its
    // "failed" view so the user can retry (which re-submits the still-selected new tpa/carrier).
    rollbackOptimisticEstimation();
    refetchEstimate();
    setPhase("failed");
  }, [rollbackOptimisticEstimation, refetchEstimate]);

  // Don't roll back the optimistic tpa/carrier the way a real failure does; the worker may yet persist it.
  const abandonTpaSync = useCallback(() => {
    toast.info("Line items are still updating. Refresh in a moment to see them.");
    refetchEstimate();
    setPhase("confirm");
    setIsTPARulesModalOpen(false);
  }, [refetchEstimate]);

  const { track: trackRecomputeJob, cancel: cancelRecomputeTracking } = useTrackedWorkerJob({
    estimateId,
    onSucceeded: finishTpaSync,
    onFailed: failTpaSync,
    onAbandoned: abandonTpaSync,
  });

  const closeTPARulesModal = useCallback(() => {
    cancelRecomputeTracking();
    setPhase("confirm");
    setIsTPARulesModalOpen(false);
    resetTpaAndCarrier();
  }, [cancelRecomputeTracking, resetTpaAndCarrier]);

  const submitNewTPAFields = useCallback(async () => {
    const tpaId = selectedTPAFields.tpaId ?? "";
    const carrierId = selectedTPAFields.carrierId ?? "";

    setPhase("saving");

    // Optimistically reflect the new tpa/carrier so the fields update immediately; remember the previous
    // estimation so a worker failure can roll it back.
    previousEstimationRef.current = queryClient.getQueryData<Estimation | null>(ESTIMATE_KEYS.detailMetadata(estimateId)) ?? null;
    if (estimation) {
      const optimistic = { ...estimation, tpaId, carrierId };
      queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), optimistic);
      dispatch(estimationActions.setEstimationState({ estimation: optimistic }));
    }

    try {
      const response = await applyTPARulesMutation({ estimateId, tpaId, carrierId, approved: true });

      if (response.queued && response.jobId) {
        // Recompute is running on the worker. Hold the dialog open in its "syncing" state; the tracker
        // resolves it once the job reaches a terminal state.
        setPhase("syncing");
        trackRecomputeJob(response.jobId, undefined);
        return;
      }

      // Ran synchronously — server truth is already up to date.
      refetchEstimate();
      setPhase("confirm");
      setIsTPARulesModalOpen(false);
    } catch {
      // The POST itself failed (mutation surfaces the error toast). Roll back and let the user retry from
      // the confirm view.
      rollbackOptimisticEstimation();
      setPhase("confirm");
    }
  }, [
    selectedTPAFields.carrierId,
    selectedTPAFields.tpaId,
    applyTPARulesMutation,
    estimateId,
    estimation,
    refetchEstimate,
    rollbackOptimisticEstimation,
    trackRecomputeJob,
  ]);

  return { isTPARulesModalOpen, phase, selectedTPAFields, updateSelectedTPAField, closeTPARulesModal, submitNewTPAFields };
}
